import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { createSsrRenderer, } from "@gsb-core/react-ssr";
export { resolveGsbHost, } from "./host.js";
export { GsbNavigationProvider, useGsbNavigation, useOptionalGsbNavigation, useGsbNavigationGuard, } from "./navigation.js";
export function createGsbApp(config) {
    const features = Object.freeze([...(config.features ?? [])]);
    const routes = Object.freeze({
        public: Object.freeze({ ...config.routes.public }),
        protected: Object.freeze({ ...config.routes.protected }),
    });
    const normalizedConfig = {
        ...config,
        features,
        routes,
    };
    const context = {
        features,
        routes,
        theme: config.theme,
        i18n: config.i18n,
        pwa: config.pwa,
        env: config.adapters.env,
        logger: config.adapters.logger,
        navigation: config.adapters.navigation,
    };
    const ClientRoot = config.adapters.client.createRoot(context);
    const ssr = createSsrRenderer({
        ...config.ssr,
        publicRoutesConfig: routes.public,
    });
    return Object.freeze({
        config: Object.freeze(normalizedConfig),
        features,
        clientRoot: ClientRoot,
        ssr,
        mount(container) {
            const element = React.createElement(ClientRoot);
            const hasServerMarkup = Array.from(container.childNodes).some((node) => node.nodeType !== 8);
            if (hasServerMarkup) {
                return hydrateRoot(container, element);
            }
            const root = createRoot(container);
            root.render(element);
            return root;
        },
    });
}
export function createSsrServer(options) {
    return {
        async render(request) {
            const { i18n } = await options.createI18n(request.url);
            const result = await options.app.ssr.render(request.url, i18n, {
                ...options.renderProviders,
                userContext: request.userContext,
                useCache: request.useCache ?? options.cache?.enabled ?? true,
            });
            return result
                ? {
                    html: result.html,
                    cached: result.cached,
                    mode: "ssr",
                    cacheInfo: result.cacheInfo,
                    helmet: result.helmet,
                }
                : { cached: false, mode: "spa" };
        },
        async health() {
            return {
                status: "healthy",
                timestamp: new Date().toISOString(),
                ...(options.health?.version ? { version: options.health.version } : {}),
                ...(options.health?.check
                    ? { checks: await options.health.check() }
                    : {}),
            };
        },
        sitemap() {
            return options.sitemap
                ? options.app.ssr.renderSitemap(options.sitemap.baseUrl)
                : null;
        },
        async start() {
            if (options.cache?.warmOnStart) {
                await options.app.ssr.warmCache(options.createI18n);
            }
            if (options.sitemap) {
                options.app.ssr.renderSitemap(options.sitemap.baseUrl);
            }
        },
    };
}
export function isStaticAssetPath(url, manifest) {
    const pathname = url.split("?")[0] ?? url;
    if (manifest?.files.includes(pathname))
        return true;
    return (pathname.startsWith("/assets/") ||
        pathname.startsWith("/icons/") ||
        pathname.startsWith("/locales/") ||
        /\.(?:js|css|png|jpe?g|svg|ico|webmanifest|txt|xml|woff2?|ttf|eot|otf|webp|avif|gif|md)$/.test(pathname));
}
export { createRequestSecurity, createSecurityHeaders, createSessionCookie, requestBodySchema, } from "./security.js";
export { createUpstreamTokenSessionVerifier } from "./server.js";
export { createReactClientAdapter } from "./client.js";
//# sourceMappingURL=index.js.map